import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { updateSchedule } from "@/lib/api";
import { localInputToIso, isoToLocalInput } from "@/lib/time";

const PRESETS = [
  { label: "5m", value: 5 },
  { label: "10m", value: 10 },
  { label: "30m", value: 30 },
  { label: "1h", value: 60 },
  { label: "1d", value: 1440 },
];

export const EditScheduleDialog = ({ schedule, onClose, onSaved }) => {
  const [message, setMessage] = useState(schedule.message);
  const [when, setWhen] = useState(isoToLocalInput(schedule.scheduled_at));
  const [soundEnabled, setSoundEnabled] = useState(schedule.sound_enabled);
  const [displayMode, setDisplayMode] = useState(schedule.display_mode || "fit");
  const [reminderEnabled, setReminderEnabled] = useState(!!schedule.reminder_minutes);
  const [reminderMinutes, setReminderMinutes] = useState(
    schedule.reminder_minutes || 10
  );
  const [repeat, setRepeat] = useState(schedule.repeat || "none");
  const [customReminder, setCustomReminder] = useState("");
  const [saving, setSaving] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSave = async () => {
    if (!message.trim()) {
      toast.error("MESSAGE REQUIRED");
      return;
    }
    const iso = localInputToIso(when);
    if (!iso) {
      toast.error("INVALID DATE / TIME");
      return;
    }
    const rm = reminderEnabled
      ? (customReminder ? parseInt(customReminder, 10) : reminderMinutes)
      : null;
    try {
      setSaving(true);
      await updateSchedule(schedule.id, {
        message: message.trim(),
        scheduled_at: iso,
        sound_enabled: soundEnabled,
        display_mode: displayMode,
        reminder_minutes: rm && rm > 0 ? rm : null,
        repeat,
        // If user edits, reset triggered flags so it re-fires
        triggered: false,
        reminder_triggered: false,
      });
      toast.success("SCHEDULE UPDATED");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error("SAVE FAILED");
    } finally {
      setSaving(false);
    }
  };

  const effectiveReminder = customReminder
    ? parseInt(customReminder, 10)
    : reminderMinutes;

  return (
    <div
      data-testid="edit-dialog"
      className="fixed inset-0 z-[9000] bg-black/60 flex items-center justify-center p-4 overflow-auto"
      onClick={onClose}
    >
      <div
        className="bg-white brutal-border brutal-shadow w-full max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between brutal-border border-t-0 border-l-0 border-r-0 p-4 bg-[#E5FF00]">
          <h3 className="font-display uppercase text-xl md:text-2xl">
            Edit Flash
          </h3>
          <button
            data-testid="edit-close"
            onClick={onClose}
            className="bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
              Message
            </label>
            <textarea
              data-testid="edit-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              maxLength={200}
              className="w-full bg-white brutal-border p-3 text-base font-display uppercase focus:outline-none focus:bg-[#FFF9B0] resize-none"
            />
          </div>

          <div>
            <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
              Date &amp; Time
            </label>
            <input
              data-testid="edit-datetime"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="w-full bg-white brutal-border p-3 font-mono focus:outline-none focus:bg-[#FFF9B0]"
            />
          </div>

          <div>
            <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
              Repeat
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "none", l: "Once" },
                { v: "daily", l: "Daily" },
                { v: "weekly", l: "Weekly" },
              ].map((r) => (
                <button
                  key={r.v}
                  type="button"
                  data-testid={`edit-repeat-${r.v}`}
                  onClick={() => setRepeat(r.v)}
                  className={`brutal-border p-2 font-display uppercase text-sm brutal-shadow-sm brutal-interactive ${
                    repeat === r.v ? "bg-[#E5FF00]" : "bg-white"
                  }`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <button
              type="button"
              data-testid="edit-reminder-toggle"
              onClick={() => setReminderEnabled((v) => !v)}
              className={`w-full flex items-center justify-between brutal-border p-3 brutal-shadow-sm brutal-interactive mb-2 ${
                reminderEnabled ? "bg-[#00CCFF]" : "bg-white"
              }`}
            >
              <span className="font-display uppercase text-sm">
                Pre-flash reminder {reminderEnabled ? "ON" : "OFF"}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest">
                {reminderEnabled ? "[enabled]" : "[disabled]"}
              </span>
            </button>
            {reminderEnabled && (
              <>
                <label className="block font-mono text-xs uppercase tracking-widest mb-2 font-bold">
                  Reminder before
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {PRESETS.map((p) => {
                    const active =
                      !customReminder && reminderMinutes === p.value;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        data-testid={`edit-preset-${p.value}`}
                        onClick={() => {
                          setReminderMinutes(p.value);
                          setCustomReminder("");
                        }}
                        className={`brutal-border px-3 py-2 font-mono text-sm uppercase brutal-shadow-sm brutal-interactive ${
                          active ? "bg-[#E5FF00]" : "bg-white"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <input
                  data-testid="edit-custom-reminder"
                  type="number"
                  min="1"
                  max="525600"
                  value={customReminder}
                  onChange={(e) => setCustomReminder(e.target.value)}
                  placeholder="Custom minutes…"
                  className="w-full bg-white brutal-border p-2 font-mono focus:outline-none focus:bg-[#FFF9B0]"
                />
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="edit-mode-fit"
              onClick={() => setDisplayMode("fit")}
              className={`brutal-border p-3 font-display uppercase text-sm brutal-shadow-sm brutal-interactive ${
                displayMode === "fit" ? "bg-[#E5FF00]" : "bg-white"
              }`}
            >
              Fit screen
            </button>
            <button
              type="button"
              data-testid="edit-mode-scroll"
              onClick={() => setDisplayMode("scroll")}
              className={`brutal-border p-3 font-display uppercase text-sm brutal-shadow-sm brutal-interactive ${
                displayMode === "scroll" ? "bg-[#E5FF00]" : "bg-white"
              }`}
            >
              Scroll
            </button>
          </div>

          <button
            type="button"
            data-testid="edit-sound"
            onClick={() => setSoundEnabled((v) => !v)}
            className={`w-full brutal-border p-3 font-display uppercase text-sm brutal-shadow-sm brutal-interactive ${
              soundEnabled ? "bg-[#E5FF00]" : "bg-white"
            }`}
          >
            Beep alarm {soundEnabled ? "ON" : "OFF"}
          </button>

          <button
            type="button"
            data-testid="edit-save"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#FF3311] text-white brutal-border brutal-shadow brutal-interactive font-display uppercase text-lg py-4 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="w-5 h-5" strokeWidth={3} />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditScheduleDialog;
